import type { Right } from '@gozoki/tools'
import type { PermissionTuple } from '~/utils/ability'
import type { PermissionRequirement, RouteTo } from '~/utils/rbac'
import type { GuardedTab } from '~/utils/rbacTabs'

/** Les cinq onglets vivent sur la même route (`/visio-reviews`), l'onglet courant est un état local. */
export type VisioReviewsTab = 'veille' | 'evaluation' | 'global' | 'boutique' | 'operateur'

/**
 * Droit v1 de tout le module, conservé tant que le flag `permission-v2` est OFF : les tuples v2 y
 * passent tous, le bitmask reste alors la seule garde effective.
 */
export const VISIO_REVIEWS_LEGACY_RIGHTS: Right[] = ['VISIO_REVIEWS']

export const INTERACTION_READ: PermissionTuple = [
  'read',
  'visio.yesterday-interactions.interaction',
]
export const EXCHANGE_READ: PermissionTuple = ['read', 'visio.exchanges-to-evaluate.exchange']
export const OVERVIEW_READ: PermissionTuple = ['read', 'visio.overview.overview']
export const ADMINISTRATION_READ: PermissionTuple = ['read', 'visio.administration.administration']
export const ADMINISTRATION_EDIT: PermissionTuple = ['edit', 'visio.administration.administration']

/**
 * Noter un échange, valider la note IA, changer son statut de résolution : les trois écritures de la
 * modale d'évaluation, ouverte depuis n'importe laquelle des trois listes du module.
 */
export const EVALUATION_EDIT: PermissionTuple = ['edit', 'visio.exchanges-to-evaluate.evaluation']

/**
 * Source unique des droits de lecture des sous-onglets. Consommée par la barre d'onglets et par le
 * `staticData.permission` de `/visio-reviews` : un seul endroit à mettre à jour, pas de dérive
 * possible entre l'onglet affiché et son contenu.
 *
 * « Vue d'ensemble », « Par Boutique » et « Par Opérateur » sont les trois angles d'une même
 * ressource, d'où le tuple partagé.
 */
export const VISIO_REVIEWS_TAB_PERMISSIONS = {
  veille: INTERACTION_READ,
  evaluation: EXCHANGE_READ,
  global: OVERVIEW_READ,
  boutique: OVERVIEW_READ,
  operateur: OVERVIEW_READ,
} as const satisfies Record<VisioReviewsTab, PermissionRequirement>

/** Accès à l'écran : dès qu'un seul de ses onglets est accessible. */
export const VISIO_REVIEWS_PAGE_READ: PermissionRequirement = {
  any: [INTERACTION_READ, EXCHANGE_READ, OVERVIEW_READ],
}

/**
 * Configuration du module (seuils de notation, critères d'évaluation IA) : n'importe quel écran la
 * lit pour colorer les notes, seule l'administration la modifie. Même exigence côté backend sur
 * `GET /visio-reviews/config`.
 */
export const VISIO_REVIEWS_CONFIG_READ: PermissionRequirement = {
  any: [ADMINISTRATION_READ, INTERACTION_READ, EXCHANGE_READ, OVERVIEW_READ],
}

/**
 * Chaque onglet est une route à part entière : son exigence est reprise ici telle qu'elle est
 * déclarée dans le `staticData.permission` de la route (même constante), pour que la barre
 * d'onglets n'ait pas à interroger le registre de routes.
 */
type VisioReviewsTabDefinition = GuardedTab<VisioReviewsTab> & {
  /** Resserré sur `string` : `AppTabs` n'accepte pas un `ReactNode` quelconque en libellé. */
  label: string
  to: RouteTo
  permission: PermissionRequirement
}

/** Ordre de déclaration = ordre d'affichage, et ordre de repli quand l'onglet actif est interdit. */
export const VISIO_REVIEWS_TABS: VisioReviewsTabDefinition[] = [
  {
    value: 'veille',
    label: 'Interactions de la Veille',
    to: '/visio-reviews/veille',
    permission: VISIO_REVIEWS_TAB_PERMISSIONS.veille,
    legacyRights: VISIO_REVIEWS_LEGACY_RIGHTS,
  },
  {
    value: 'evaluation',
    label: 'Échanges à Évaluer',
    to: '/visio-reviews/evaluation',
    permission: VISIO_REVIEWS_TAB_PERMISSIONS.evaluation,
    legacyRights: VISIO_REVIEWS_LEGACY_RIGHTS,
  },
  {
    value: 'global',
    label: "Vue d'ensemble",
    to: '/visio-reviews/global',
    permission: VISIO_REVIEWS_TAB_PERMISSIONS.global,
    legacyRights: VISIO_REVIEWS_LEGACY_RIGHTS,
  },
  {
    value: 'boutique',
    label: 'Par Boutique',
    to: '/visio-reviews/boutique',
    permission: VISIO_REVIEWS_TAB_PERMISSIONS.boutique,
    legacyRights: VISIO_REVIEWS_LEGACY_RIGHTS,
  },
  {
    value: 'operateur',
    label: 'Par Opérateur',
    to: '/visio-reviews/operateur',
    permission: VISIO_REVIEWS_TAB_PERMISSIONS.operateur,
    legacyRights: VISIO_REVIEWS_LEGACY_RIGHTS,
  },
]
